// src/components/PushSettings.jsx
// Preferences → Phone and app notifications (3.52.1; backend lib/push.js).
// Turn them on for THIS device (the device asks for permission), pick the
// kinds (saved to the account, for every device), see the devices they're
// on, send a test. A notification never shows a fax number, name or file
// name. iPhone/iPad: only the installed app can get them.
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import Emo from './Emo';
import { usePreferences } from '../hooks/usePreferences';
import { pushSupported, pushNeedsInstall, pushBlocked, currentSubscription, turnOn, turnOff, devices as loadDevices, sendTest } from '../utils/push';
import { formatDate } from '../utils/format';

// The kinds and their defaults (the owner's, Oct 10); the role must allow
// the matching fax pop-up (Roles & Access); Health: Super Admins only
const KINDS = [
  { id: 'received', label: 'A new fax arrives (that you can see in Inbox)', perm: 'popups.received', on: true },
  { id: 'failed', label: 'A fax you sent didn’t go through', perm: 'popups.failed', on: true },
  { id: 'delivered', label: 'A fax you sent was delivered', perm: 'popups.delivered', on: false },
  { id: 'retry', label: 'A fax you sent will be tried again by itself', perm: 'popups.failed', on: false },
  { id: 'health', label: 'Health alerts (the ones admin@ is emailed)', superAdmin: true, on: true },
  { id: 'notes', label: 'A reminder from My notes is due', on: true },   // 3.74.0 (everyone)
];

export default function PushSettings({ styles: S, superAdmin }) {
  const { getToken, can, viewAs } = useAuth();
  const { prefs, setPreference } = usePreferences();
  const [here, setHere] = useState(null);     // this device's subscription (null = off)
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);       // { tone, text }
  const supported = pushSupported();
  const needsInstall = pushNeedsInstall();

  // 3.86.4 (owner): on only if the server still has this device, not just the browser
  const isHere = (sub, rows) => !!sub && rows.some(d => d.endpointEnd && sub.endpoint.endsWith(d.endpointEnd));
  const refresh = useCallback(async () => {
    const sub = await currentSubscription();
    try {
      const rows = (await loadDevices(getToken)).devices || [];
      setList(rows);
      setHere(isHere(sub, rows) ? sub : null);
    } catch { setHere(sub); /* the list is extra */ }
  }, [getToken]);
  useEffect(() => { if (supported) refresh(); }, [supported, refresh]);

  const kinds = KINDS.filter(k => (k.superAdmin ? superAdmin : (superAdmin || !k.perm || can(k.perm))));
  const chosen = { ...Object.fromEntries(KINDS.map(k => [k.id, k.on])), ...(prefs.push || {}) };

  async function act(what, fn, okText) {
    setBusy(what); setMsg(null);
    try { await fn(); if (okText) setMsg({ tone: 'ok', text: okText }); await refresh(); }
    catch (e) { setMsg({ tone: 'error', text: e.message }); }
    setBusy('');
  }

  // A dead device is dropped by the test itself, so check it's still there
  async function testHere() {
    await sendTest(getToken);
    const sub = await currentSubscription();
    if (!isHere(sub, (await loadDevices(getToken)).devices || [])) {
      throw new Error('Notifications stopped working on this device. Please tap Turn on for this device to set them up again.');
    }
  }

  return (
    <section style={S.row}>
      <span style={S.name}><Emo e="📲" /> Phone and app notifications</span>
      <p style={S.help}>Notifications on your phone or computer, even when the Fax Portal is closed. Tap one to open that fax. They never show a fax number, a name or a file name, only the line, pages and times.</p>

      {viewAs ? <p style={S.help}>Not while using View as: notifications are each person’s own.</p>
        : !supported ? <p style={S.help}>This browser can’t show notifications. On a phone, install the app (Help → Install the app) and turn them on there.</p>
        : needsInstall ? <p style={S.help}>On iPhone and iPad, notifications work in the installed app: choose <strong>Install the app</strong> under Help, open CP Fax Portal from your Home Screen, then turn them on here.</p>
        : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {here
              ? <>
                <span style={S.onPill}>● On for this device</span>
                <button style={S.btnLight} disabled={!!busy} onClick={() => act('test', testHere, 'Test sent. It should appear in a few seconds.')}>{busy === 'test' ? 'Sending…' : 'Send a test'}</button>
                <button style={S.btnLight} disabled={!!busy} onClick={() => act('off', () => turnOff(getToken), 'Turned off for this device.')}>{busy === 'off' ? 'Turning off…' : 'Turn off'}</button>
              </>
              : <button style={S.btnMain} disabled={!!busy || pushBlocked()} onClick={() => act('on', () => turnOn(getToken), 'Turned on for this device.')}>{busy === 'on' ? 'Turning on…' : 'Turn on for this device'}</button>}
            {!here && pushBlocked() && <p style={S.help}>Notifications are blocked for the Fax Portal on this device. Allow them in the device’s or browser’s settings first.</p>}
          </div>
        )}
      {msg && <p role="status" style={{ ...S.help, color: msg.tone === 'error' ? 'var(--t-b71c1c)' : 'var(--t-2e7d32)' }}>{msg.text}</p>}

      {kinds.length > 0 && (
        <fieldset style={{ ...S.row, marginTop: 4, paddingTop: 6 }}>
          <legend style={S.name}>Which notifications (on every device)</legend>
          {kinds.map(k => (
            <label key={k.id} style={S.choice}>
              <input type="checkbox" checked={!!chosen[k.id]} onChange={e => setPreference('push', { ...chosen, [k.id]: e.target.checked })} />
              <span>{k.label}</span>
            </label>
          ))}
        </fieldset>
      )}

      {list.length > 0 && (
        <div>
          <span style={S.name}>Your devices with notifications on</span>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--t-546e7a)', lineHeight: 1.7 }}>
            {list.map(d => <li key={d.id}>{d.device}{here && d.endpointEnd && here.endpoint.endsWith(d.endpointEnd) ? ' · this one' : ''} <span style={{ color: 'var(--t-90a4ae)' }}>· since {formatDate(d.createdAt)}</span></li>)}
          </ul>
          <p style={{ ...S.help, marginTop: 4 }}>To stop one, open the Fax Portal on it and choose Turn off, or turn off notifications for it in the device’s settings.</p>
        </div>
      )}
    </section>
  );
}
